import {
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import TableHeadCell from 'components/TableHeadCell'
import SettingsSchoolManagementListItem from './ListItem'
import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { DeleteIcon, EditIcon } from 'components/Icons'
import Confirm from 'components/Dialog'
import { destroy } from 'store/settings/campus'
import { useDispatch } from 'react-redux'

const basePath = '/settings/school-management'

export default function SettingsSchoolManagementList({ districts, campuses, schools }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [campusList, setCampusList] = useState([])
  const [selected, setSelected] = useState()

  useEffect(() => {
    if (selected) {
      setCampusList(campuses.filter(campus => campus.district.id === selected))
    }
  }, [selected, campuses])

  return (
    <Box sx={{ p: 1 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={5} lg={4}>
          <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
            <Box sx={{ p: 2, bgcolor: 'background.default', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="subtitle1" fontWeight="600">
                {t('Districts')}
              </Typography>
              <Button size="small" variant="contained" color="primary" onClick={() => navigate(`${basePath}/district/new`)}>
                {t('Add District')}
              </Button>
            </Box>
            <Table>
              <TableHead>
                <TableRow>
                  <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('Code')}</TableHeadCell>
                  <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('Name')}</TableHeadCell>
                  <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('Abbreviation')}</TableHeadCell>
                  <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('Action')}</TableHeadCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {districts.map((item, index) => (
                  <SettingsSchoolManagementListItem
                    key={index}
                    item={item}
                    selected={selected}
                    setSelected={setSelected}
                  />
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12} md={7} lg={8}>
          <Paper variant="outlined" sx={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
            {selected ? (
              <Campuses list={campusList} selected={selected} schools={schools} />
            ) : (
              <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center', justifyItems: 'center', p: 4 }}>
                <Typography variant="body1" color="textSecondary" align="center" sx={{ width: '100%' }}>
                  {t('Select a district from the list to view and manage its campuses.')}
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

function Campuses({ list, selected, schools }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [confirm, setConfirm] = useState({ status: false, id: null })

  const onConfirm = () => {
    setConfirm(false)
    dispatch(destroy({ id: confirm?.id }))
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight="600">{t('Campuses')}</Typography>
        <Button
          size="small"
          variant="contained"
          color="primary"
          onClick={() => navigate(`${basePath}/district/${selected}/campus/new`)}
        >
          {t('Add Campus')}
        </Button>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('Bldg Code')}</TableHeadCell>
              <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('School')}</TableHeadCell>
              <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('Name')}</TableHeadCell>
              <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('Address')}</TableHeadCell>
              <TableHeadCell sx={{ fontWeight: '500 !important' }}>{t('Action')}</TableHeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {list.length > 0 ? (
              list.map((item, index) => (
                <TableRow hover key={index}>
                  <TableCell>{item?.buildingCode}</TableCell>
                  <TableCell>{schools.find(school => school.id === item?.schoolId)?.name}</TableCell>
                  <TableCell>{item?.name}</TableCell>
                  <TableCell>{item?.address ? `${item?.address?.street}, ${item?.address?.city}, ${item?.address?.state}` : ''}</TableCell>
                  <TableCell>
                    <Stack flexDirection={'row'}>
                      <IconButton size="small" component={NavLink} to={`${basePath}/district/${selected}/campus/${item.id}`}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setConfirm({ status: true, id: item.id })
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="textSecondary">
                    {t('No campuses found for this district.')}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <Confirm open={confirm?.status} onClose={() => setConfirm({ status: false, id: null })} onConfirm={onConfirm} />
    </Box>
  )
}